import PublicLayout from "@/components/PublicLayout";
import {
  ShieldCheck, Lock, KeyRound, Cloud, Activity, UserCheck, Database,
  CreditCard, Sparkles,
} from "lucide-react";

const Block = ({ icon: Icon, title, body }) => (
  <div className="border border-[#262626] bg-[#141414] rounded-sm p-6 flex flex-col gap-3">
    <div className="w-10 h-10 rounded-sm border border-[#262626] bg-[#0A0A0A] flex items-center justify-center">
      <Icon className="w-4 h-4 text-[#32D74B]" strokeWidth={1.6}/>
    </div>
    <h3 className="font-display text-base text-white tracking-tight">{title}</h3>
    <p className="text-[13px] text-[#A3A3A3] leading-relaxed">{body}</p>
  </div>
);

export default function SecurityPage() {
  return (
    <PublicLayout>
      <section className="border-b border-[#1F1F1F]">
        <div className="max-w-5xl mx-auto px-6 md:px-8 py-16 md:py-24">
          <div className="kpi-label mb-3">/ SECURITY · TRUST</div>
          <h1 className="font-display text-4xl md:text-6xl tracking-tighter text-white leading-[1.02] max-w-4xl">
            Enterprise-grade security, <span className="text-[#32D74B]">privacy-first by design.</span>
          </h1>
          <p className="text-[#A3A3A3] mt-6 text-[16px] md:text-[18px] leading-relaxed max-w-3xl">
            RADAR handles sensitive go-to-market intelligence on behalf of Business Development teams.
            We treat your account data, internal notes, and team activity the way you would —
            encrypted, scoped, and audited.
          </p>
        </div>
      </section>

      {/* Platform Security */}
      <section className="max-w-5xl mx-auto px-6 md:px-8 py-14">
        <div className="kpi-label mb-3">/ PLATFORM SECURITY</div>
        <h2 className="font-display text-3xl md:text-4xl text-white tracking-tighter">
          How we protect your data.
        </h2>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3 mt-10">
          <Block icon={Lock} title="Encrypted in Transit" body="All RADAR traffic is served over HTTPS/TLS 1.3. Session cookies are httpOnly, secure, and SameSite-strict — never readable by client JS." />
          <Block icon={Database} title="Encrypted at Rest" body="Reports, activity logs and subscription data live in managed MongoDB with encryption-at-rest enabled at the cluster level." />
          <Block icon={KeyRound} title="Secure Authentication" body="Single sign-on via Google OAuth — no app-managed passwords. Sessions auto-expire after 7 days; logout revokes them instantly server-side." />
          <Block icon={UserCheck} title="Role-Based Access" body="Manager / BDR roles are enforced server-side. BDRs only see their own pipeline; managers see consolidated team analytics. No client-only gating." />
          <Block icon={Activity} title="Audit-Ready" body="Every report, activity, and plan change is stamped with user_id, name, email and timestamp — so you can answer 'who ran this, when' on demand." />
          <Block icon={ShieldCheck} title="Quota & Abuse Limits" body="Server-side, atomic quota enforcement on every metered action (reports, downloads, enhancements). Concurrent races are race-safe via Mongo conditional updates." />
        </div>
      </section>

      {/* Trusted Integrations */}
      <section className="max-w-5xl mx-auto px-6 md:px-8 py-14 border-t border-[#1F1F1F]">
        <div className="kpi-label mb-3">/ TRUSTED INTEGRATIONS</div>
        <h2 className="font-display text-3xl md:text-4xl text-white tracking-tighter">
          Vetted partners only.
        </h2>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3 mt-10">
          <Block icon={UserCheck} title="Google OAuth" body="Identity & SSO — Google's industry-leading auth surface. We never see or store your password." />
          <Block icon={Sparkles} title="Anthropic Claude (Haiku 4.5)" body="All intelligence synthesis runs on Claude. We send the public context + your primary inputs; no training on your data." />
          <Block icon={Cloud} title="Tavily" body="Real-time web search for live citations. Queries are anonymous; we don't share user identity with Tavily." />
          <Block icon={CreditCard} title="Razorpay & Stripe (Phase 2)" body="PCI-compliant payment gateways. RADAR never stores card details — we only receive a tokenised reference." />
          <Block icon={Database} title="MongoDB Atlas" body="Managed, encrypted, regionally-isolated database backbone with point-in-time backups." />
          <Block icon={Activity} title="Resend" body="Outbound digest email delivery. Transactional only; no marketing lists; no third-party tracking pixels." />
        </div>
      </section>

      {/* Responsible AI */}
      <section className="max-w-5xl mx-auto px-6 md:px-8 py-14 border-t border-[#1F1F1F]">
        <div className="kpi-label mb-3">/ RESPONSIBLE AI</div>
        <h2 className="font-display text-3xl md:text-4xl text-white tracking-tighter max-w-3xl">
          AI that's <span className="text-[#0A84FF]">grounded, transparent, and auditable.</span>
        </h2>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-3 mt-10">
          <Block icon={ShieldCheck} title="Citation-First" body="Every factual claim in a RADAR report is grounded on Tavily-sourced web snippets. Section 21 of every report shows the URLs that backed the synthesis." />
          <Block icon={Activity} title="Confidence Levels" body="Each report carries an explicit confidence rating + an 'Assumptions Made' list — so you always know what the AI is sure of vs. inferring." />
          <Block icon={UserCheck} title="Human-in-the-Loop" body="RADAR produces drafts and recommendations — never final decisions. The user-facing UX surface always reminds you to validate before acting." />
        </div>
      </section>

      {/* Infrastructure */}
      <section className="max-w-5xl mx-auto px-6 md:px-8 py-14 border-t border-[#1F1F1F]">
        <div className="kpi-label mb-3">/ INFRASTRUCTURE</div>
        <h2 className="font-display text-3xl md:text-4xl text-white tracking-tighter">Built to scale.</h2>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-3 mt-10">
          <Block icon={Cloud} title="Cloud-Native" body="RADAR runs on a Kubernetes-managed cloud cluster with auto-scaling, isolated workloads, and zero-downtime deploys." />
          <Block icon={Activity} title="Observability" body="Structured logs, health probes, and an unprotected /api/health endpoint for uptime monitors. Background generation status is queryable per-report." />
          <Block icon={Lock} title="Secrets Management" body="API keys, LLM tokens, and DB URIs live in environment variables — never in source control, never shipped to the client bundle." />
        </div>
      </section>

      <section className="max-w-3xl mx-auto px-6 md:px-8 py-14 text-center border-t border-[#1F1F1F]">
        <div className="kpi-label mb-3">/ QUESTIONS?</div>
        <p className="text-[15px] text-[#A3A3A3] leading-relaxed">
          Want our security questionnaire, DPA, or to discuss enterprise data isolation?
          Reach our team at <a href="mailto:support@radaracctintel.com" className="text-[#0A84FF] hover:underline">support@radaracctintel.com</a>.
        </p>
      </section>
    </PublicLayout>
  );
}
